import { Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import { cloneElement, KeyboardEventHandler } from 'react';
import { WrappedFieldProps } from 'redux-form';
import styled, { css } from 'styled-components';

import FormError from 'dashboard/components/FormControls/FormError';
import Input, { InputProps } from 'design-system/components/Input';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const DisplayWrapper = styled.div<{ editable: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${(props) =>
    props.editable &&
    css`
      cursor: pointer;
      &:hover {
        transition-duration: ${TransitionDuration.Medium};
        svg {
          display: block;
        }
      }
    `}
`;

const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.sm};
  ${paragraphMixins.md};
`;

const StyledWrapper = styled.div`
  min-height: ${Spacing[7]};
`;

const StyledEditIcon = styled(EditIcon)`
  max-width: ${Spacing[4]};
  margin-left: 6px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.color.variants.basic.highlightDark};
`;

interface InputWithHoverEditFieldProps extends WrappedFieldProps, InputProps {
  displayElement: JSX.Element;
}

const InputWithHoverEditField = ({
  className,
  input,
  meta: { error, submitFailed, touched },
  autoFocus,
  placeholder,
  disabled,
  displayElement,
  id,
  ...passthroughProps
}: InputWithHoverEditFieldProps) => {
  const hasError = !!(error && (submitFailed || touched));
  const [editing, { enable: enableEditing, disable: disableEditing }] = useToggle();

  // render placeholder if field is null or empty string
  const displayValue = input.value == null || input.value === '' ? placeholder : input.value;
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (['Enter', 'Escape', 'Tab'].includes(e.key)) {
      e.preventDefault();
      disableEditing();
    }
  };

  return (
    <>
      <StyledWrapper className={className}>
        {editing ? (
          <StyledInput
            id={id}
            {...input}
            {...passthroughProps}
            autoFocus={autoFocus}
            hasError={hasError}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onBlur={disableEditing}
          />
        ) : (
          <DisplayWrapper
            id='input-hover-edit__display-wrapper'
            editable={!disabled}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onClick={disabled ? null : enableEditing}
          >
            {
              // clone element for render style, but centralize content display
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              cloneElement(displayElement, null, displayValue)
            }
            {!disabled && <StyledEditIcon />}
          </DisplayWrapper>
        )}
      </StyledWrapper>
      {hasError && <FormError>{error}</FormError>}
    </>
  );
};

export default InputWithHoverEditField;
